import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import {
  STATUTS_SAISON, moisDeSaison, nomMois, iso, nbJoursDuMois, jourSemaine,
  parseIso, aujourdhui, JOURS_COURTS, fetchAll,
} from '../../utils/planning'
import './ConstructeurPlanning.css'
import Combobox from '../../components/Combobox'

const TYPES = [
  { code: 'ACCUEIL', label: 'Accueil', ouvert: 'accueil_ouvert' },
  { code: 'SUPERVISION', label: 'Supervision', ouvert: 'supervision_ouverte' },
  { code: 'HYDRO', label: 'Hydro', ouvert: 'hydro_ouvert' },
]
const CODES = TYPES.map(t => t.code)

const RESET_NOTIF = { notifie_le: null, relance_le: null, accuse_le: null }

// Etat d'une case (jour x poste) : alertes calculees a l'affichage, jamais stockees.
function etatCase(date, type, jours, affs, dispos) {
  const jour = jours[date]
  const row = affs[`${date}|${type.code}`] || null
  const supId = row && row.statut !== 'annule' ? row.superviseur_id : null
  const ouvert = !!(jour && jour[type.ouvert])
  const rouges = []
  let ambre = false
  if (supId) {
    if (!ouvert) rouges.push('Poste fermé ce jour')
    if (dispos[`${supId}|${date}`] === false) rouges.push('Dispo = NON')
    const nb = CODES.filter(c => {
      const r = affs[`${date}|${c}`]
      return r && r.statut !== 'annule' && r.superviseur_id === supId
    }).length
    if (nb > 1) rouges.push('Double poste')
  } else if (ouvert) {
    ambre = true
  }
  const muet = !!(row && row.verifie)
  return { row, supId, ouvert, rouges, ambre, muet }
}

export default function ConstructeurPlanning() {
  const { profile } = useAuth()
  const [saisons, setSaisons] = useState([])
  const [saisonId, setSaisonId] = useState(null)
  const [mois, setMois] = useState(null)
  const [sups, setSups] = useState([])
  const [jours, setJours] = useState({})
  const [affs, setAffs] = useState({})
  const [dispos, setDispos] = useState({})
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [seulesAlertes, setSeulesAlertes] = useState(false)
  const [noteOuverte, setNoteOuverte] = useState(null)
  const [publication, setPublication] = useState(false)

  const saison = saisons.find(s => s.id === saisonId) || null

  useEffect(() => {
    let annule = false
    supabase.from('planning_saisons').select('*').order('date_debut', { ascending: false }).then(({ data, error }) => {
      if (annule) return
      if (error) {
        setMsg({ type: 'error', text: 'Impossible de charger les saisons.' })
        setLoading(false)
        return
      }
      const liste = data || []
      setSaisons(liste)
      const auj = aujourdhui()
      const defaut = liste.find(s => s.date_debut <= auj && auj <= s.date_fin) || liste.find(s => s.statut === 'construction') || liste.find(s => s.statut === 'publie') || liste[0]
      setSaisonId(defaut ? defaut.id : null)
      if (!defaut) setLoading(false)
    })
    return () => { annule = true }
  }, [])

  useEffect(() => {
    if (!saison) return
    const liste = moisDeSaison(saison)
    const t = parseIso(aujourdhui())
    setMois(liste.find(m => m.annee === t.annee && m.mois === t.mois) || liste[0])
  }, [saison])

  const listeMois = useMemo(() => (saison ? moisDeSaison(saison) : []), [saison])

  const charger = useCallback(async () => {
    if (!mois) return
    setLoading(true)
    const debut = iso(mois.annee, mois.mois, 1)
    const fin = iso(mois.annee, mois.mois, nbJoursDuMois(mois.annee, mois.mois))
    const [rSup, rJ, rA, rD] = await Promise.all([
      supabase.from('profiles').select('id, prenom, nom, actif').eq('role', 'superviseur').order('prenom'),
      fetchAll(() => supabase.from('jours_planning').select('*').gte('date', debut).lte('date', fin).order('date')),
      fetchAll(() => supabase.from('affectations').select('*').in('type', CODES).gte('date', debut).lte('date', fin).order('date').order('type')),
      fetchAll(() => supabase.from('dispos_annuelles').select('superviseur_id, date, dispo')
        .gte('date', debut).lte('date', fin).order('superviseur_id').order('date')),
    ])
    if (rSup.error || rJ.error || rA.error || rD.error) {
      setMsg({ type: 'error', text: 'Erreur lors du chargement du mois.' })
    }
    const mJ = {}
    for (const j of rJ.data) mJ[j.date] = j
    const mA = {}
    for (const a of rA.data) mA[`${a.date}|${a.type}`] = a
    const mD = {}
    for (const d of rD.data) mD[`${d.superviseur_id}|${d.date}`] = d.dispo
    setSups(rSup.data || [])
    setJours(mJ)
    setAffs(mA)
    setDispos(mD)
    setLoading(false)
  }, [mois])

  useEffect(() => { charger() }, [charger])

  const lignes = useMemo(() => {
    if (!mois) return []
    const res = []
    const n = nbJoursDuMois(mois.annee, mois.mois)
    for (let j = 1; j <= n; j++) {
      const date = iso(mois.annee, mois.mois, j)
      const cases = TYPES.map(t => etatCase(date, t, jours, affs, dispos))
      res.push({ date, jsem: jourSemaine(date), jour: jours[date] || null, cases })
    }
    return res
  }, [mois, jours, affs, dispos])

  const stats = useMemo(() => {
    let rouges = 0
    let aPourvoir = 0
    let nonPublies = 0
    for (const l of lignes) {
      for (const c of l.cases) {
        if (c.rouges.length > 0 && !c.muet) rouges += 1
        if (c.ambre) aPourvoir += 1
        if (c.supId && c.row && !c.row.publie_le) nonPublies += 1
      }
    }
    return { rouges, aPourvoir, nonPublies }
  }, [lignes])

  function majLocal(date, type, patch) {
    const cle = `${date}|${type}`
    setAffs(prev => ({ ...prev, [cle]: { ...prev[cle], ...patch } }))
  }

  async function affecter(date, type, supId) {
    setMsg(null)
    const cle = `${date}|${type}`
    const existant = affs[cle]
    if (!supId) {
      if (!existant) return
      const patch = { superviseur_id: null, statut: 'a_pourvoir', verifie: false, note: null, ...RESET_NOTIF }
      const { error } = await supabase.from('affectations').update(patch).eq('id', existant.id)
      if (error) { setMsg({ type: 'error', text: "Erreur lors de l'enregistrement." }); return }
      majLocal(date, type, patch)
      return
    }
    if (existant) {
      const patch = { superviseur_id: supId, statut: 'prevu', verifie: false, note: null, ...RESET_NOTIF }
      const { error } = await supabase.from('affectations').update(patch).eq('id', existant.id)
      if (error) { setMsg({ type: 'error', text: "Erreur lors de l'enregistrement." }); return }
      majLocal(date, type, patch)
      return
    }
    const { data, error } = await supabase.from('affectations')
      .insert({ date, type, superviseur_id: supId, statut: 'prevu', cree_par: profile ? profile.id : null })
      .select().single()
    if (error) { setMsg({ type: 'error', text: "Erreur lors de l'enregistrement." }); return }
    setAffs(prev => ({ ...prev, [cle]: data }))
  }

  async function basculerVerifie(date, type) {
    const row = affs[`${date}|${type}`]
    if (!row) return
    const verifie = !row.verifie
    const { error } = await supabase.from('affectations').update({ verifie }).eq('id', row.id)
    if (error) { setMsg({ type: 'error', text: "Erreur lors de l'enregistrement." }); return }
    majLocal(date, type, { verifie })
  }

  async function enregistrerNote(date, type, texte) {
    const row = affs[`${date}|${type}`]
    setNoteOuverte(null)
    if (!row) return
    const note = texte.trim() || null
    if (note === (row.note || null)) return
    const { error } = await supabase.from('affectations').update({ note }).eq('id', row.id)
    if (error) { setMsg({ type: 'error', text: "Erreur lors de l'enregistrement de la note." }); return }
    majLocal(date, type, { note })
  }

  function bornesMois() {
    return {
      debut: iso(mois.annee, mois.mois, 1),
      fin: iso(mois.annee, mois.mois, nbJoursDuMois(mois.annee, mois.mois)),
    }
  }

  async function publier() {
    setMsg(null)
    setPublication(true)
    const { debut, fin } = bornesMois()
    const { error } = await supabase.from('affectations')
      .update({ publie_le: new Date().toISOString() })
      .in('type', CODES).gte('date', debut).lte('date', fin)
      .not('superviseur_id', 'is', null).is('publie_le', null)
    setPublication(false)
    if (error) { setMsg({ type: 'error', text: 'Erreur lors de la publication.' }); return }
    setMsg({ type: 'success', text: 'Planning du mois publié : les superviseurs voient leurs dates.' })
    charger()
  }

  async function depublier() {
    if (!window.confirm('Retirer ce mois de la vue des superviseurs ?')) return
    setMsg(null)
    setPublication(true)
    const { debut, fin } = bornesMois()
    const { error } = await supabase.from('affectations')
      .update({ publie_le: null })
      .in('type', CODES).gte('date', debut).lte('date', fin)
    setPublication(false)
    if (error) { setMsg({ type: 'error', text: 'Erreur lors du retrait.' }); return }
    setMsg({ type: 'success', text: 'Mois retiré de la vue des superviseurs.' })
    charger()
  }

  function optionsPour(date, supIdActuel) {
    const liste = sups.filter(s => s.actif || s.id === supIdActuel).map(s => {
      const d = dispos[`${s.id}|${date}`]
      const rang = d === true ? 0 : d === undefined ? 1 : 2
      const suffixe = d === true ? ' (dispo)' : d === false ? ' (NON)' : ' (?)'
      return { id: s.id, rang, label: `${s.prenom} ${s.nom}${suffixe}` }
    })
    liste.sort((a, b) => a.rang - b.rang || a.label.localeCompare(b.label, 'fr'))
    return liste
  }

  if (!loading && !saison) {
    return (
      <div>
        <div className="page-header"><div><h2>Construire le planning</h2></div></div>
        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
        <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
          Aucune saison de planning. Exécute d'abord la migration supabase-planning.sql.
        </div>
      </div>
    )
  }

  const visibles = seulesAlertes
    ? lignes.filter(l => l.cases.some(c => (c.rouges.length > 0 && !c.muet) || c.ambre))
    : lignes
  const dejaPublie = lignes.some(l => l.cases.some(c => c.row && c.row.publie_le))

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Construire le planning</h2>
          <div className="page-header-sub">
            {saison ? `Saison ${saison.libelle} (${STATUTS_SAISON[saison.statut]})` : ''}
          </div>
        </div>
        {saisons.length > 1 && (
          <select value={saisonId || ''} onChange={e => setSaisonId(e.target.value)}>
            {saisons.map(s => <option key={s.id} value={s.id}>{s.libelle}</option>)}
          </select>
        )}
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="cp-bar">
        <select
          value={mois ? `${mois.annee}-${mois.mois}` : ''}
          onChange={e => {
            const [a, m] = e.target.value.split('-').map(Number)
            setMois({ annee: a, mois: m })
          }}
        >
          {listeMois.map(m => (
            <option key={`${m.annee}-${m.mois}`} value={`${m.annee}-${m.mois}`}>
              {nomMois(m.mois)} {m.annee}
            </option>
          ))}
        </select>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={seulesAlertes} onChange={e => setSeulesAlertes(e.target.checked)} />
          Jours avec alerte uniquement
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {dejaPublie && (
            <button className="btn btn-outline btn-sm" onClick={depublier} disabled={publication}>
              Retirer la publication
            </button>
          )}
          <button className="btn btn-bordeaux" onClick={publier} disabled={publication || stats.nonPublies === 0}>
            {publication ? 'Publication...' : `Publier (${stats.nonPublies})`}
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="s-label">Alertes rouges</div>
          <div className="s-value text-bordeaux">{stats.rouges}</div>
          <div className="s-sub">double poste, dispo NON, poste fermé</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Postes à pourvoir</div>
          <div className="s-value">{stats.aPourvoir}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Non publiés</div>
          <div className="s-value">{stats.nonPublies}</div>
          <div className="s-sub">invisibles des superviseurs</div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="card">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Jour</th>
                {TYPES.map(t => <th key={t.code}>{t.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--stone)', padding: 24 }}>Aucun jour à afficher</td></tr>
              ) : visibles.map(l => (
                <tr key={l.date} className={l.jsem >= 5 ? 'cp-weekend' : ''}>
                  <td className="cp-date">
                    {JOURS_COURTS[l.jsem]} {parseIso(l.date).jour}
                    {l.jour && l.jour.ferie && <span className="cp-tag">Férié</span>}
                    {l.jour && l.jour.accueil_majore && <span className="cp-tag">Majoré</span>}
                    {l.jour && l.jour.libelle && <div className="cp-notetxt">{l.jour.libelle}</div>}
                  </td>
                  {TYPES.map((t, i) => {
                    const c = l.cases[i]
                    if (!c.ouvert && !c.supId) {
                      return <td key={t.code} className="cp-cell"><span className="cp-ferme">Fermé</span></td>
                    }
                    const alerte = c.rouges.length > 0 && !c.muet
                    const cle = `${l.date}|${t.code}`
                    return (
                      <td key={t.code} className={`cp-cell ${alerte ? 'cp-rouge' : c.ambre ? 'cp-ambre' : ''}`}>
                        <Combobox
                          className="cp-select"
                          value={c.supId || ''}
                          placeholder="À pourvoir"
                          onChange={v => affecter(l.date, t.code, v)}
                          options={optionsPour(l.date, c.supId).map(o => ({ value: o.id, label: o.label }))}
                        />
                        {alerte && <div className="cp-alertes">{c.rouges.join(' · ')}</div>}
                        {c.ambre && <div className="cp-alertes">Poste à pourvoir</div>}
                        {c.supId && (
                          <div className="cp-actions">
                            {(c.rouges.length > 0 || c.muet) && (
                              <button
                                type="button"
                                className={`cp-mini ${c.muet ? 'on' : ''}`}
                                onClick={() => basculerVerifie(l.date, t.code)}
                              >
                                {c.muet ? 'Vérifié ✓' : 'Marquer vérifié'}
                              </button>
                            )}
                            <button type="button" className="cp-mini" onClick={() => setNoteOuverte(cle)}>
                              {c.row.note ? 'Note ✎' : 'Note'}
                            </button>
                            {c.row.publie_le && <span className="cp-pub">publié</span>}
                          </div>
                        )}
                        {noteOuverte === cle && (
                          <input
                            className="cp-note"
                            autoFocus
                            defaultValue={c.row ? c.row.note || '' : ''}
                            placeholder="Note courte (Entrée pour valider)"
                            onBlur={e => enregistrerNote(l.date, t.code, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                          />
                        )}
                        {noteOuverte !== cle && c.row && c.row.note && <div className="cp-notetxt">{c.row.note}</div>}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
